import { HYDRATE } from 'next-redux-wrapper';

import {
    TChangePaymentDayInfo,
} from '@r1-frontend/api-domru/api-profile/v1/payments/change-payment-day-info/dto/ResponseDto';
import { TModelsTypes } from '@r1-frontend/api-domru/api-profile/v2/info/all';
import {
    IContact,
    IPaymentInfo,
    IPhoneContact,
    IProducts,
} from '@r1-frontend/api-domru/api-profile/v2/info/all/dto/ResponseDto';
import { IProducts as IClientProducts } from '@r1-frontend/api-domru/api-profile/v2/info/products';
import { UserAgreementDto } from '@r1-frontend/api-domru/core-profile/v2/user/agreements';
import { ICityState } from '@r1-frontend/entities/stores/city/reducer';

import { IAction } from '@r1-frontend/shared/types/Redux';

import * as ActionCreators from '../actionCreators';
import { EAuthTypes } from '../actionTypes';

export type { IClientProducts };

export interface IClientProductsState extends IProducts {
    loaded: number,
}

export interface IClientContact extends IContact {
    value: string,
}

export interface IClientPhoneContact extends IPhoneContact {
    value: string,
}

export interface IClientContacts {
    email: IClientContact[],
    phone: IClientPhoneContact[],
}

export interface IClientContactsState extends IClientContacts {
    loaded: number,
}

export type TAgreement = UserAgreementDto
export type TAgreements = TAgreement[]
export type TAgreementsStatus = 'init' | 'loading' | 'complete' | 'error'

export interface IAuthState {
    isAuth: boolean,
    accessToken: string,
    refreshToken: string,
    loginError: string,
    clientPersonal: IClientPersonal | null,
    clientInfoStage: {
        [key in TModelsTypes]: EStage;
    },
    /** @deprecated use clientInfoStage **/
    clientPaymentFetching: boolean,
    /** @deprecated use clientInfoStage **/
    clientPaymentLoaded: number | null,
    clientPayment: IPaymentInfo | null,

    /** @deprecated use clientInfoStage **/
    isClientProductsLoading: boolean,
    clientProducts: IClientProductsState | null,
    productType: number,

    /** @deprecated use clientInfoStage **/
    isClientContactsLoading: boolean,
    clientContacts: IClientContactsState | null,

    paymentDayInfo: TChangePaymentDayInfo | null,

    clientSignMark: boolean,
    clientHasAgreement: boolean,

    csrf: string | null,

    agreements: {
        status: TAgreementsStatus,
        list: TAgreements,
    },
}

export interface IAddress {
    street: string,
    streetId: number,
    house: number,
    building: string,
    flat: number,
    city?: string,
}

export interface IClientPersonal {
    agreementId: number,
    agreement: number,
    agreementTypeId: number,
    fio: string,
    photoUrl: string,
    address: IAddress,
    cityId: number,
}

export enum EStage {
    init = 'init',
    loading = 'loading',
    loaded = 'loaded',
    error = 'error',
}
export const initialState: IAuthState = {
    isAuth: false,
    accessToken: '',
    refreshToken: '',
    loginError: '',

    clientPersonal: null,
    clientInfoStage: {
        payment: EStage.init,
        contacts: EStage.init,
        products: EStage.init,
        personal: EStage.init,
    },
    clientPaymentFetching: false,
    clientPaymentLoaded: null,
    clientPayment: null,

    isClientProductsLoading: false,
    clientProducts: null,
    productType: 0,

    isClientContactsLoading: false,
    clientContacts: null,

    paymentDayInfo: null,

    clientSignMark: false,
    clientHasAgreement: false,

    csrf: null,

    agreements: {
        status: 'init',
        list: [],
    },
};

type InferValueTypes<T> = T extends { [key: string]: infer U } ? U : never;

type TActions = ReturnType<InferValueTypes<typeof ActionCreators>>

export type TState = { auth: IAuthState, city: ICityState }
export type THydrateAction = IAction<typeof HYDRATE, TState>

export default (state = initialState, action: TActions | THydrateAction): IAuthState => {
    switch (action.type) {
        case HYDRATE: {
            return {
                ...state,
                ...action.payload.auth,
            };
        }

        case EAuthTypes.SET_LOGIN_ERROR: {
            return {
                ...state,
                loginError: action.payload,
            };
        }
        case EAuthTypes.SET_CLIENT_INFO_STAGE: {
            return {
                ...state,
                clientInfoStage: {
                    ...state.clientInfoStage,
                    ...action.payload,
                },
            };
        }

        case EAuthTypes.SET_CLIENT_PERSONAL: {
            return {
                ...state,
                clientPersonal: action.payload,
            };
        }

        case EAuthTypes.FETCH_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: true,
            };
        }

        case EAuthTypes.SET_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: false,
                clientPayment: action.payload,
                clientPaymentLoaded: (new Date()).getTime(),
            };
        }

        case EAuthTypes.ERROR_CLIENT_PAYMENT: {
            return {
                ...state,
                clientPaymentFetching: false,
            };
        }

        case EAuthTypes.SET_CLIENT_PRODUCTS: {
            return {
                ...state,
                clientProducts: {
                    ...action.payload,
                    loaded: (new Date()).getTime(),
                },
            };
        }

        case EAuthTypes.CLIENT_PRODUCTS_LOADING:
            return {
                ...state,
                isClientProductsLoading: action.payload,
            };

        case EAuthTypes.SET_CLIENT_CONTACTS: {
            return {
                ...state,
                clientContacts: {
                    ...action.payload,
                    loaded: (new Date()).getTime(),
                },
            };
        }

        case EAuthTypes.CLIENT_CONTACTS_LOADING:
            return {
                ...state,
                isClientContactsLoading: action.payload,
            };

        case EAuthTypes.SET_PAYMENT_DAY_INFO: {
            return {
                ...state,
                paymentDayInfo: action.payload,
            };
        }

        case EAuthTypes.INIT_AUTH: {
            return {
                ...state,
                ...action.payload,
            };
        }
        case EAuthTypes.SET_IS_AUTH: {
            return {
                ...state,
                isAuth: action.payload,
            };
        }
        case EAuthTypes.LOGIN: {
            return {
                ...state,
                isAuth: true,
                accessToken: action.payload.accessToken,
                refreshToken: action.payload.refreshToken ??
                (state.refreshToken
                    ? state.refreshToken
                    : action.payload.accessToken),
                loginError: '',
            };
        }

        case EAuthTypes.SET_CSRF_TOKEN: {
            return {
                ...state,
                csrf: action.payload,
            };
        }

        case EAuthTypes.SET_STATUS: {
            return {
                ...state,
                agreements: {
                    ...state.agreements,
                    status: action.payload,
                },
            };
        }
        case EAuthTypes.SET_AGREEMENTS: {
            return {
                ...state,
                agreements: {
                    ...state.agreements,
                    list: action.payload,
                },
            };
        }

        default: {
            return state;
        }
    }
};
